import React, { useEffect, useState } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router-dom';

import ProductCard from '../general/ProductCard.js';
import {WithAlgolia, useAlgoliaClient, useAlgoliaProductIndex} from '../algolia/WithAlgolia';
import Hero2 from './StartPageAssets/hero-2.png';
import './OfferPage.scss';
import './FavoritesPage.scss';
import {FormattedMessage, useIntl} from 'react-intl';
import { userSelectors } from 'features/userSlice';
import { favoriteListsActions, favoriteListsSelectors } from 'features/favoriteListsSlice';
import { favoriteListErrorMessageId } from 'helper/favoriteListErrors';
import { useValidFavoriteListWineCount } from 'helper/useValidFavoriteListWineCount';
import { ReactComponent as IconClose } from 'components/icons/close.svg';
import { ReactComponent as IconEdit } from 'components/icons/edit.svg';
import { ReactComponent as HeartIcon } from 'components/icons/heart.svg';
import { ReactComponent as HeartFullIcon } from 'components/icons/heart-full.svg';
import { ReactComponent as GlassIcon } from 'components/icons/glass.svg';

const FavoritesPage = () => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const algoliaClient = useAlgoliaClient();
    const productIndex = useAlgoliaProductIndex();
    const productIndexReady = productIndex && productIndex.appId;

    const soldTo = useSelector(userSelectors.soldTo);
    const favoriteLists = useSelector(favoriteListsSelectors.favoriteLists);
    const favoriteListsRequest = useSelector(favoriteListsSelectors.favoriteListsRequest);
    const activeListId = useSelector(favoriteListsSelectors.activeListId);
    const activeListWines = useSelector(favoriteListsSelectors.activeListWines);
    const activeList = favoriteLists && favoriteLists.find(list => list.id === activeListId);
    const validListWineCount = useValidFavoriteListWineCount();

    useEffect(() => {
        if (soldTo != null && favoriteListsRequest.status === 'idle') {
            dispatch(favoriteListsActions.fetchFavoriteLists({soldTo: soldTo}));
        }
    }, [soldTo, favoriteListsRequest.status]);

    const [listProducts, setListProducts] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (productIndexReady && activeListWines) {
            if (activeListWines.length === 0) {
                setListProducts([]);
            } else {
                productIndex.getObjects(activeListWines.map(wine => wine.wineId)).then((result) => {
                    if (!cancelled) {
                        setListProducts(result.results);
                    }
                });
            }
        }
        return () => {
            cancelled = true;
        };
    }, [productIndexReady, activeListWines, productIndex]);

    const externalByWineId = {};
    activeListWines.forEach(wine => {
        externalByWineId[wine.wineId] = wine.external;
    });

    const validWineCount = listProducts ? listProducts.filter(product => product && product.objectID).length : 0;

    const [submitting, setSubmitting] = useState(false);

    const [creatingList, setCreatingList] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [createErrorId, setCreateErrorId] = useState(null);
    const handleCreateList = async () => {
        const trimmed = newListName.trim();
        if (!trimmed || submitting) return;
        setSubmitting(true);
        setCreateErrorId(null);
        try {
            const created = await dispatch(favoriteListsActions.createFavoriteList({soldTo, name: trimmed})).unwrap();
            dispatch(favoriteListsActions.setActiveFavoriteList(created.id));
            setCreatingList(false);
            setNewListName('');
        } catch (err) {
            setCreateErrorId(favoriteListErrorMessageId(err?.status));
        } finally {
            setSubmitting(false);
        }
    };

    const [deleteConfirming, setDeleteConfirming] = useState(false);
    const handleDeleteList = async () => {
        if (!activeList || submitting) return;
        setSubmitting(true);
        try {
            await dispatch(favoriteListsActions.deleteFavoriteList({soldTo, favoriteListId: activeList.id})).unwrap();
            setDeleteConfirming(false);
        } finally {
            setSubmitting(false);
        }
    };

    const [renaming, setRenaming] = useState(false);
    const [renameName, setRenameName] = useState('');
    const [renameErrorId, setRenameErrorId] = useState(null);
    const startRenaming = () => {
        setRenameName(activeList ? activeList.name : '');
        setRenameErrorId(null);
        setRenaming(true);
    };
    const handleRename = async () => {
        const trimmed = renameName.trim();
        if (!trimmed || !activeList || submitting) return;
        setSubmitting(true);
        setRenameErrorId(null);
        try {
            await dispatch(favoriteListsActions.renameFavoriteList({
                soldTo, favoriteListId: activeList.id, name: trimmed, description: activeList.description,
            })).unwrap();
            setRenaming(false);
        } catch (err) {
            setRenameErrorId(favoriteListErrorMessageId(err?.status));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <WithAlgolia>
            {algoliaClient && productIndexReady ?
                <div className="container-fluid px-md-3">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-9">
                            <div className="offer-page-hero mb-4" style={{backgroundImage: `url(${Hero2})`}}>
                                <h1>
                                    <FormattedMessage
                                        id="FavoritesPage.Favorites"
                                        values={{
                                            ts: Date.now(),
                                            breakingLine: <br/>}}
                                    />
                                </h1>
                            </div>

                            {favoriteLists &&
                                <div className="favorite-list-tabs d-flex flex-wrap mb-4">
                                    {favoriteLists.map(list => (
                                        <button
                                            key={list.id}
                                            type="button"
                                            className={`favorite-list-tab rounded-pill ${list.id === activeListId ? 'active' : ''}`}
                                            onClick={() => {
                                                dispatch(favoriteListsActions.setActiveFavoriteList(list.id));
                                                setDeleteConfirming(false);
                                                setRenaming(false);
                                            }}
                                        >
                                            <HeartFullIcon className="favorite-list-tab-heart" />
                                            {list.name}&nbsp;<span className="badge">{validListWineCount(list)}</span>
                                        </button>
                                    ))}

                                    {creatingList ?
                                        <span className="new-list-form d-flex align-items-center">
                      <input
                          type="text"
                          className="new-list-input-field"
                          autoFocus
                          placeholder={intl.formatMessage({id: 'FavoriteListsPopover.NewListPlaceholder'})}
                          value={newListName}
                          disabled={submitting}
                          onChange={ev => setNewListName(ev.target.value)}
                          onKeyDown={ev => ev.key === 'Enter' && handleCreateList()}
                      />
                      <button type="button" className="new-list-create-btn" disabled={submitting || newListName.trim() === ''} onClick={handleCreateList}>
                        <FormattedMessage id="FavoriteListsPopover.Create" />
                      </button>
                      <span className="cursor-pointer new-list-close" onClick={() => {
                          setCreatingList(false);
                          setNewListName('');
                      }}>
                        <IconClose style={{width: '14px', height: '14px'}} />
                      </span>
                    </span>
                                        :
                                        <button
                                            type="button"
                                            className="favorite-list-tab rounded-pill new-list-tab"
                                            onClick={() => setCreatingList(true)}
                                        >
                                            + <FormattedMessage id="FavoritesPage.NewList" />
                                        </button>
                                    }
                                </div>
                            }
                            {createErrorId && <div className="text-danger small mb-3"><FormattedMessage id={createErrorId} /></div>}

                            {activeList &&
                                <div className="favorite-list-header d-flex align-items-center mb-3">
                                    {renaming ?
                                        <span className="rename-list-form d-flex align-items-center">
                      <input
                          type="text"
                          className="rename-input-field"
                          autoFocus
                          value={renameName}
                          disabled={submitting}
                          onChange={ev => setRenameName(ev.target.value)}
                          onKeyDown={ev => ev.key === 'Enter' && handleRename()}
                      />
                      <button type="button" className="rename-save-btn" disabled={submitting || renameName.trim() === ''} onClick={handleRename}>
                        <FormattedMessage id="FavoritesPage.Save" />
                      </button>
                      <span className="cursor-pointer rename-close" onClick={() => setRenaming(false)}>
                        <IconClose style={{width: '14px', height: '14px'}} />
                      </span>
                    </span>
                                        :
                                        <>
                                            <h5 className="mb-0">
                                                {activeList.name}
                                                {(listProducts === null || validWineCount > 0) &&
                                                    <small className="text-muted favorite-list-wine-count">
                            <FormattedMessage id="FavoriteListsPopover.WineCount" values={{count: validWineCount}} />
                          </small>
                                                }
                                            </h5>
                                            {!activeList.isDefault &&
                                                <>
                                                    <span
                                                        className="cursor-pointer ms-2 small"
                                                        title={intl.formatMessage({id: 'FavoritesPage.Rename'})}
                                                        onClick={startRenaming}>
                            <IconEdit style={{width: '14px', height: '14px'}} />
                          </span>
                                                    {(listProducts === null || validWineCount > 0) &&
                                                        (!deleteConfirming ?
                                                            <span className="cursor-pointer ms-2 small" onClick={() => setDeleteConfirming(true)}>
                                <IconClose style={{width: '14px', height: '14px'}} />
                              </span>
                                                            :
                                                            <span className="ms-2 small">
                                <FormattedMessage id="FavoritesPage.DeleteConfirm" />&nbsp;
                                                                <span className="cursor-pointer text-danger" onClick={handleDeleteList}>
                                  <FormattedMessage id="FavoritesPage.Yes" />
                                </span>
                                                                &nbsp;/&nbsp;
                                                                <span className="cursor-pointer" onClick={() => setDeleteConfirming(false)}>
                                  <FormattedMessage id="FavoritesPage.No" />
                                </span>
                              </span>
                                                        )
                                                    }
                                                </>
                                            }
                                        </>
                                    }
                                </div>
                            }
                            {renameErrorId && <div className="text-danger small mb-3"><FormattedMessage id={renameErrorId} /></div>}

                            <div className="row px-0">
                                {listProducts && listProducts.length > 0 ?
                                    listProducts.map((product) => (product && product.objectID) && (
                                        <div key={product.objectID} className="col-xs-12 col-md-6 col-lg-6 col-xl-4 my-3">
                                            <ProductCard
                                                product={product}
                                                shadow
                                                forceBorder
                                                favoritesView
                                                favoriteListId={activeListId}
                                                favoritesViewExternal={externalByWineId[product.objectID] === true}
                                            />
                                        </div>
                                    ))
                                    :
                                    <div className="favorite-list-empty text-center">
                                        <div className="empty-icon-circle">
                                            <HeartIcon />
                                        </div>
                                        <h5><FormattedMessage id="FavoritesPage.EmptyTitle" /></h5>
                                        <p className="text-muted"><FormattedMessage id="FavoritesPage.EmptySubtitle" /></p>
                                        <div className="empty-actions d-flex justify-content-center flex-wrap">
                                            {activeList && !activeList.isDefault &&
                                                <button type="button" className="delete-list-btn" disabled={submitting} onClick={handleDeleteList}>
                                                    <IconClose style={{width: '14px', height: '14px'}} />
                                                    <FormattedMessage id="FavoritesPage.DeleteList" />
                                                </button>
                                            }
                                            <Link to="/products/all" className="go-to-assortment-btn">
                                                <GlassIcon />
                                                <FormattedMessage id="FavoritesPage.GoToAssortment" />
                                            </Link>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                :
                <div className="container-fluid px-md-3 text-center">
                    loading...
                </div>}
        </WithAlgolia>
    );
};

export default FavoritesPage;
